import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux';
import DataTable from '../../elements/DataTable';
import EmptyState from '../../elements/EmptyState'
import Loader from '../../elements/Loader';
import ChevronIcon from '../../elements/icons/ChevronIcon';
import { unSlugify } from '../../../utils'
import { tableHeadersFields } from '../../../utils';
import ModalDialog from '../../layouts/ModalDialog';
import NewSickbayVisit from './NewSickbayVisit';
import UpdateStudentMedicals from './UpdateStudentMedicals';
import { clearCreatedSickBayRecord, clearUpdatedSickBayRecord, fetchSickBayRecords, fetchSickBayStats } from '../../../store/actions/sickbayActions';
import { updateStudent } from '../../../store/actions/studentsActions';

const StudentMedicals = ({student, reload}) => {
  const [updatingMedicals, setUpdatingMedicals] = useState(false);
  const [recordingVisit, setRecordingVisit] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [period, setPeriod] = useState('30d');
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20
  })

  const sickbaySelector = useSelector((state) => state.sickbay)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(fetchSickBayRecords(`student=${student._id}`, pagination.page, pagination.perPage))
  }, [dispatch, pagination.page, pagination.perPage, student._id])

  useEffect(() => {
    dispatch(fetchSickBayStats(`student=${student._id}&period=${period}`))
  }, [dispatch, period, student._id])

  useEffect(() => {
    if (sickbaySelector.createdSickBayRecord !== null) {
      dispatch(clearCreatedSickBayRecord())
      setRecordingVisit(false)
      dispatch(fetchSickBayRecords(`student=${student._id}`, pagination.page, pagination.perPage))
      dispatch(fetchSickBayStats(`student=${student._id}&period=${period}`))
    }

    if (sickbaySelector.updatedSickBayRecord !== null) {
      dispatch(clearUpdatedSickBayRecord())
      dispatch(fetchSickBayRecords(`student=${student._id}`, pagination.page, pagination.perPage))
      dispatch(fetchSickBayStats(`student=${student._id}&period=${period}`))
    }
  }, [
    dispatch,
    pagination.page,
    pagination.perPage,
    period,
    sickbaySelector.createdSickBayRecord,
    sickbaySelector.updatedSickBayRecord,
    student._id
  ])

  const normalizeRecords = (recordsPayload) => {
    if (!recordsPayload) return []
    if (Array.isArray(recordsPayload)) return recordsPayload
    if (Array.isArray(recordsPayload.records)) return recordsPayload.records
    if (Array.isArray(recordsPayload.data)) return recordsPayload.data
    return []
  }

  const normalizedRecords = normalizeRecords(sickbaySelector.sickBayRecords)

  const cleanupRecordsData = (dataSet) => {
    if (!dataSet) return []

    return dataSet.map((record) => {
      const outcomeLabel = (unSlugify(record.outcome || '') || 'Unknown').replace(/\b\w/g, (char) => char.toUpperCase())

      return {
      visitDate: <p className='text-sm text-gray-600'>{moment(record.visitDate).format('MMMM Do YYYY')}<br/><span className='text-xs text-gray-400'>{moment(record.visitDate).format('h:mm a')}</span></p>,
      symptoms: <div className='flex flex-wrap gap-[5px]'>{(record.symptoms || []).slice(0, 4).map((symptom, symptomIndex) => <span key={symptomIndex} className='text-xs px-2 py-1 rounded bg-gray-100 text-gray-700'>{symptom}</span>)}{record.symptoms?.length > 4 && <span className='text-xs text-gray-500'>+{record.symptoms.length - 4} more</span>}</div>,
      diagnosis: <p className='text-sm text-gray-700'>{record.diagnosis || 'Not provided'}</p>,
      outcome: <span className='text-xs px-2 py-1 rounded bg-vcm-light-purple bg-opacity-20 text-vcm-purple'>{outcomeLabel}</span>,
      followUp: <p className='text-sm text-gray-600'>{record.followUp?.required ? `Yes${record.followUp?.date ? ` - ${moment(record.followUp.date).format('MMM Do YYYY')}` : ''}` : 'No'}</p>,
      medications: <p className='text-sm text-gray-600'>{record.medicationsDispensed?.length || 0}</p>
      }
    })
  }

  const columnWidths = {
    visitDate: 'w-2/12',
    symptoms: 'w-3/12',
    diagnosis: 'w-3/12',
    outcome: 'w-2/12',
    followUp: 'w-1/12',
    medications: 'w-1/12'
  }

  const tableOptions = {
    selectable: false,
    clickableRows: false,
    rowAction: () => {}
  }

  const stats = sickbaySelector.sickBayStats || {}
  const overview = stats.overview || {}

  const periodOptions = [
    { label: '7 days', value: '7d' },
    { label: '30 days', value: '30d' },
    { label: '90 days', value: '90d' },
    { label: '1 year', value: '365d' }
  ]

  const renderRankBars = (title, data, countField) => {
    const max = data && data.length > 0 ? Math.max(...data.map((item) => item[countField])) : 0

    return (
      <div className='border rounded p-4'>
        <h4 className='text-sm font-medium text-gray-700 mb-3'>{title}</h4>
        {!data || data.length === 0 ? (
          <p className='text-sm text-gray-400'>No data yet</p>
        ) : (
          <div className='space-y-3'>
            {data.map((item, itemIndex) => (
              <div key={itemIndex}>
                <div className='w-full flex items-center justify-between mb-1'>
                  <p className='text-sm text-gray-700 capitalize'>{item._id}</p>
                  <p className='text-xs text-gray-500'>{item[countField]}</p>
                </div>
                <div className='w-full h-[7px] rounded bg-gray-100 overflow-hidden'>
                  <div className='h-full bg-vcm-purple rounded' style={{ width: `${max > 0 ? (item[countField] / max) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <div className='w-full'>
        <div className='w-full flex items-start justify-between'>
          <div className='w-1/2'>
            <h4 className='font-medium text-lg text-gray-700'>Student Medicals</h4>
            <p className='text-sm mt-1'>Medical Information for this student.</p>
          </div>
          {/* <button onClick={()=>{setUpdatingMedicals(true)}} className='bg-vcm-purple px-[18px] py-[12px] rounded-[8px] text-vcm-light-purple text-sm hover:bg-black transition duration-200'>
            Update Medicals
          </button> */}
        </div>

        <div className='mt-[20px]'>
          {!student.medical ? 
            <div>
              <EmptyState emptyStateText={`No medical information provided for this student yet. Click on the "Update medicals" button above to provide information`} />
            </div>

            :
            <>
              <div className='w-full'>
                <label className='text-xs tracking-[0.2em] uppercase text-gray-400 mb-2 block'>blood group</label>
                <p className='text-gray-600'>{student.medical.bloodGroup ? student.medical.bloodGroup : <span className='text-gray-400 text-sm'>Not provided</span> }</p>
              </div>

              <div className='w-full mt-4'>
                <label className='text-xs tracking-[0.2em] uppercase text-gray-400 mb-2 block'>Pre-existing medical conditions</label>
                <p className='text-gray-600'>{student.medical?.preExistingConditions?.conditions?.length > 0 ? student.medical.preExistingConditions?.conditions?.join(', ') : <span className='text-gray-400 text-sm'>No pre-existing conditions indicated for this student</span> }</p>
              </div>

              <div className='w-full mt-4'>
                <label className='text-xs tracking-[0.2em] uppercase text-gray-400 mb-2 block'>Allergies</label>
                <p className='text-gray-600'>{student.medical?.allergies?.length > 0 ? student.medical.allergies.join(', ') : <span className='text-gray-400 text-sm'>No allergies indicated for this student</span> }</p>
              </div>

              <div className='w-full mt-4'>
                <label className='text-xs tracking-[0.2em] uppercase text-gray-400 mb-2 block'>Inoculations</label>
                <div className='w-full flex flex-nowrap items-center gap-x-[10px]'>
                  {student.medical.inoculations && student.medical.inoculations.length > 0 ?student.medical.inoculations.map((inoculation, inoculationIndex)=>(
                    <div key={inoculationIndex} className='px-[15px] py-[7px] bg-gray-100 rounded'>
                      <p className='text-gray-600 text-sm capitalize'>
                        {unSlugify(inoculation.type)}
                      </p>
                      <p className='text-xs text-gray-500'>{new Date(inoculation.date).toDateString()}</p>
                    </div>
                  ))
                  :
                    <p className='text-gray-400 text-sm'>No inoculation information provided for this student yet</p>
                  }
                </div>
              </div>
            </>
          }
        </div>

        <div className='mt-[40px] pt-[40px] border-t'>
          <div className='w-full flex items-start justify-between'>
            <div className='w-1/2'>
              <p className='uppercase tracking-[0.2em] text-sm text-vcm-purple'>Sickbay Visits</p>
              <p className='text-sm mt-[5px]'>A record of sickbay visits for this student and treatments administered. You can record a new sickbay visit by clicking on "Record Sickbay Visit"</p>
            </div>
            <button onClick={()=>{setRecordingVisit(true)}} className='bg-vcm-purple px-[18px] py-[12px] rounded-[8px] text-vcm-light-purple text-sm hover:bg-black transition duration-200'>
              Record Sickbay Visit
            </button>
          </div>

          <div className='w-full mt-6 border rounded p-4'>
            <div className='w-full flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-gray-700'>Sickbay stats</p>
                <p className='text-xs text-gray-500 mt-1'>Student-specific stats for the selected period.</p>
              </div>
              <button onClick={() => setShowStats(!showStats)} className='text-sm text-gray-600 flex items-center gap-x-2'>
                {showStats ? 'Collapse' : 'Expand'}
                <ChevronIcon className={`w-4 h-4 transition duration-200 ${showStats ? 'rotate-[270deg]' : 'rotate-[90deg]'}`} />
              </button>
            </div>

            {showStats && (
              <>
                <div className='w-full flex flex-wrap items-center gap-2 mt-4'>
                  {periodOptions.map((option, optionIndex) => (
                    <button
                      key={optionIndex}
                      onClick={() => setPeriod(option.value)}
                      className={`px-3 py-2 text-xs rounded transition duration-200 ${period === option.value ? 'bg-vcm-purple text-vcm-light-purple' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {sickbaySelector.loadingSickBayStats ? (
                  <div className='mt-6'>
                    <Loader />
                  </div>
                ) : (
                  <>
                    <div className='grid grid-cols-2 md:grid-cols-4 gap-3 mt-5'>
                      <div className='rounded border p-3'>
                        <p className='text-xs uppercase tracking-[0.12em] text-gray-400'>Total Visits</p>
                        <p className='text-xl font-semibold text-gray-700 mt-1'>{overview.totalRecords || 0}</p>
                      </div>
                      <div className='rounded border p-3'>
                        <p className='text-xs uppercase tracking-[0.12em] text-gray-400'>Visits This Week</p>
                        <p className='text-xl font-semibold text-gray-700 mt-1'>{overview.visitsThisWeek || 0}</p>
                      </div>
                      <div className='rounded border p-3'>
                        <p className='text-xs uppercase tracking-[0.12em] text-gray-400'>Visits This Month</p>
                        <p className='text-xl font-semibold text-gray-700 mt-1'>{overview.visitsThisMonth || 0}</p>
                      </div>
                      <div className='rounded border p-3'>
                        <p className='text-xs uppercase tracking-[0.12em] text-gray-400'>Referred Cases</p>
                        <p className='text-xl font-semibold text-gray-700 mt-1'>{overview.referredCases || 0}</p>
                      </div>
                      <div className='rounded border p-3'>
                        <p className='text-xs uppercase tracking-[0.12em] text-gray-400'>Emergency Transfers</p>
                        <p className='text-xl font-semibold text-gray-700 mt-1'>{overview.emergencyTransfers || 0}</p>
                      </div>
                      <div className='rounded border p-3'>
                        <p className='text-xs uppercase tracking-[0.12em] text-gray-400'>Avg Symptoms / Visit</p>
                        <p className='text-xl font-semibold text-gray-700 mt-1'>{overview.averageSymptomsPerVisit || 0}</p>
                      </div>
                      <div className='rounded border p-3'>
                        <p className='text-xs uppercase tracking-[0.12em] text-gray-400'>Follow Ups Required</p>
                        <p className='text-xl font-semibold text-gray-700 mt-1'>{stats.followUps?.required || 0}</p>
                      </div>
                      <div className='rounded border p-3'>
                        <p className='text-xs uppercase tracking-[0.12em] text-gray-400'>Total Admissions</p>
                        <p className='text-xl font-semibold text-gray-700 mt-1'>{stats.admissions?.totalAdmissions || 0}</p>
                      </div>
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mt-5'>
                      {renderRankBars('Top Symptoms', stats.symptoms?.topSymptoms || [], 'count')}
                      {renderRankBars('Outcomes', stats.outcomes || [], 'count')}
                      {renderRankBars('Top Medications', stats.medications?.topMedications || [], 'count')}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <div className='w-full mt-[30px]'>
            {sickbaySelector.loadingSickBayRecords ? (
              <Loader />
            ) : normalizedRecords.length > 0 ? (
              <DataTable
                tableHeaders={tableHeadersFields(cleanupRecordsData(normalizedRecords)[0]).headers}
                tableData={cleanupRecordsData(normalizedRecords)}
                columnWidths={columnWidths}
                columnDataStyles={{}}
                allFields={tableHeadersFields(cleanupRecordsData(normalizedRecords)[0]).fields}
                onSelectItems={() => {}}
                tableOptions={tableOptions}
                pagination={{
                  perPage: pagination.perPage,
                  currentPage: pagination.page,
                  totalItems: sickbaySelector.sickBayRecords?.total || normalizedRecords.length
                }}
                changePage={(page) => setPagination({ ...pagination, ...{ page } })}
                updatePerPage={(perPage) => setPagination({ ...pagination, ...{ perPage } })}
              />
            ) : (
              <EmptyState emptyStateText={`No sickbay visits recorded for this student yet. Click on the "Record Sickbay Visit" button above to provide information`} />
            )}
          </div>
        </div>

      </div>

      <ModalDialog
        shown={updatingMedicals} 
        closeFunction={()=>{setUpdatingMedicals(false)}} 
        actionFunction={()=>{}} 
        actionFunctionLabel={``}
        dialogTitle={`Update medical information for ${student?.firstName} ${student.lastName}`}
        maxWidthClass='max-w-3xl'
        hideActions={true}
      >
        <UpdateStudentMedicals
            medicals={student?.medical || {}}
            reload={()=>reload()}
            closeFunction={()=>{setUpdatingMedicals(false)}} 
            submitMedicals={(payload) => {dispatch(updateStudent(student._id, {medical: payload}))}}
        />

      </ModalDialog>

      <ModalDialog
        shown={recordingVisit}
        closeFunction={()=>{setRecordingVisit(false)}}
        actionFunction={()=>{}}
        actionFunctionLabel={``}
        dialogTitle={`Record sickbay visit for ${student?.firstName} ${student?.lastName}`}
        maxWidthClass='max-w-4xl'
        hideActions={true}
      >
        <NewSickbayVisit
          close={() => setRecordingVisit(false)}
          student={student._id}
        />
      </ModalDialog>
    </>
  )
}

export default StudentMedicals